import type { BuyerSendVersionDto } from "@/api/buyerSendVersions";
import { StatusBadge } from "@/design-system/components/ui";
import { combinedIntroCopy } from "../combinedIntro/combinedIntroCopy";

/**
 * The vocabulary the two lists and the detail pane share, so a version reads the
 * same wherever it is rendered.
 */

/**
 * How the message reached its recipient.
 *
 * 🔴 `selfAttested === null` means NOT RECORDED, never "Phoenix". Every version
 * carried in from before the send-history table existed has one, because the
 * path was never stored — and asserting "Phoenix" about all of them would be
 * confidently wrong about a fact nobody established.
 */
export function pathLabel(version: BuyerSendVersionDto): string {
  if (version.selfAttested === null) return "Not recorded";
  return version.selfAttested ? "Own email" : "Phoenix";
}

/**
 * A missing value renders as an em dash — never "N/A", never "None", never an
 * empty cell, which reads as a rendering bug (Table and Tile Standard §7).
 *
 * <p>Nothing here is an ACTIONABLE absence: a recipient nobody recorded in 2025
 * is not work anyone can pick up, so it carries no door.</p>
 */
export function Blank() {
  return <span className="text-gray-mid">—</span>;
}

/**
 * The one exception badge this surface uses.
 *
 * <p>`warn`, not `danger`: a version whose details predate versioning is a thing
 * worth knowing about, not a breach. Amber for attention, red for overdue
 * (§6.4) — and there is nothing overdue on a record of what already
 * happened.</p>
 *
 * <p>Inline after the version's own name rather than in a column of its own,
 * because it is a fact about that row's subject and a dedicated column is empty
 * on every ordinary row (§6.3).</p>
 */
export function CarriedInBadge() {
  return (
    <StatusBadge tone="warn" title="Carried in from before send history was recorded, so only its body and date are known.">
      Partial record
    </StatusBadge>
  );
}

/**
 * D4 — "Sent with {N} other listings" for a version that went out as part of a
 * combined intro, or null for an ordinary one.
 *
 * <p>The version's body is then the WHOLE combined email — this listing among
 * the others — which is what the buyer actually received, so it is stored and
 * shown whole. This line is what tells a reader why the body names businesses
 * that are not this listing. `combinedListingCount` includes this listing, so
 * the others are one fewer.</p>
 */
export function combinedOthersLabel(version: BuyerSendVersionDto): string | null {
  if (version.combinedIntroEmailId == null || version.combinedListingCount == null) return null;
  return combinedIntroCopy.sentWithOthers(Math.max(0, version.combinedListingCount - 1));
}

/** `v3`, `v2`, `v1` — the version's short name, used as its heading. */
export function versionLabel(version: BuyerSendVersionDto): string {
  return `v${version.versionNumber}`;
}

/*
 * 🔴 WHY THE PACKAGE CARD CARRIES NO THUMBNAIL AND NO PAGE COUNT.
 *
 * Neither is derivable, and both would be fabrications if rendered. A package
 * PDF is not stored: `BuyerPackageService.GetPdfAsync` RE-RENDERS it on demand
 * from the submission's CURRENT narrative, terms and letter. So the only PDF
 * this app can produce for an old version is a document that did not exist when
 * that version was sent, and putting its first page on a March card would show a
 * reader a page March's broker never received — while looking exactly like
 * evidence.
 *
 * The card therefore shows what IS known first-hand: the letter enclosed, the
 * attachment's name, the recipient, the path and the date. Getting a real
 * thumbnail means storing the rendered artifact per send, which is a genuine
 * feature with a storage cost and a retention question, not a rendering detail.
 */
